// pages/Community/SkoolNudges.jsx
//
// The "post one line in <tab>" invitations sent to quiet members — at most two
// per person, then silence — per member, with their prospect state and whether
// another nudge can still come. Engaged / close / booked / converted / not
// interested people, and anyone who introduced themselves, get no more.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import addNotification from "helpers/notify";
import { getSkoolNudges, setSkoolProspectState } from "api/connectors/connectors";
import { COLUMNS as PROSPECT_STATES } from "./SkoolProspects";

const PROSPECT = Object.fromEntries(PROSPECT_STATES.map((c) => [c.key, c]));
const SOURCE_LABEL = { manual: "set by you", booking: "booking", rule: "rule", ai: "AI" };
const TAB = { projects: "Projects", progress: "Progress", problems: "Problems to solve" };
const PHASE = {
  open: { label: "May get another", color: "#9a6700" },
  blocked: { label: "No more nudges", color: "#1a7f37" },
  done: { label: "Both sent — done", color: "#667085" },
};
const STATUS_COLOR = { success: "#1a7f37", queued: "#9a6700", running: "#9a6700", failed: "#b42318", cancelled: "#98a2b3" };
const fmt = (d) => { try { return d ? new Date(d).toLocaleString() : "—"; } catch (_) { return "—"; } };
const blockedLabel = (b) => (b === "introduced" ? "introduced" : b === "onboarding" ? "onboarding ladder member" : (PROSPECT[String(b).replace("prospect:", "")] || {}).label || b);

export default function SkoolNudges({ brandId }) {
  const [rows, setRows] = useState([]);
  const [max, setMax] = useState(2);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState("");
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSkoolNudges(brandId ? { brandId } : {});
      setRows((res && res.members) || []);
      if (res && res.max) setMax(res.max);
    } catch (e) {
      addNotification(e?.message || e?.error || "Could not load nudges", "error");
    } finally { setLoading(false); }
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    if (!filter) return rows;
    if (PHASE[filter]) return rows.filter((m) => m.phase === filter);
    return rows.filter((m) => m.prospect && m.prospect.state === filter);
  }, [rows, filter]);
  const count = (ph) => rows.filter((m) => m.phase === ph).length;

  const changeState = async (p, state) => {
    if (!p || !state) return;
    try {
      await setSkoolProspectState(p._id, state, state === "auto" ? "" : "set from Skool nudges");
      addNotification(state === "auto" ? "Handed back to the classifier" : `Now: ${(PROSPECT[state] || {}).label || state}`, "success");
      load();
    } catch (e) { addNotification(e?.error || e?.message || "Could not change the state", "error"); }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-2" style={{ gap: 10, flexWrap: "wrap" }}>
        <h6 className="mb-0">Skool nudges</h6>
        <span className="text-muted small" data-testid="nudges-summary">
          {rows.length} members nudged · {count("open")} may get another · {count("blocked")} no more · {count("done")} got both ({max} max)
        </span>
        <select className="form-control form-control-sm ml-auto" style={{ width: 210 }} aria-label="Nudges filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All nudged members</option>
          {Object.entries(PHASE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          {PROSPECT_STATES.map((x) => <option key={x.key} value={x.key}>Now: {x.label}</option>)}
        </select>
        <button className="btn btn-link btn-sm p-0" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
      </div>
      <div className="text-muted small mb-2">Nudges go to existing members only — people who joined after the onboarding ladder started are onboarding's.</div>

      {!shown.length && !loading && <div className="text-muted small">{rows.length ? "Nothing matches this filter." : "Nobody has been nudged for this brand."}</div>}

      {shown.map((m) => {
        const p = m.prospect;
        const pc = p ? (PROSPECT[p.state] || PROSPECT.new) : null;
        const ph = PHASE[m.phase] || PHASE.open;
        const key = `${m.brand_id}|${m.userId}`;
        const isOpen = open === key;
        return (
          <div key={key} data-testid={`nudge-${m.userId}`} style={{ border: "1px solid #e4e7ec", borderRadius: 8, padding: "8px 10px", marginBottom: 8, background: "#fff" }}>
            <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap", cursor: "pointer" }} onClick={() => setOpen(isOpen ? "" : key)}>
              <b className="small">{m.name || m.handle || m.userId}</b>
              {pc && <span data-testid="nudge-prospect-pill" title={`${pc.label} (${SOURCE_LABEL[p.stateSource] || p.stateSource})${p.reason ? ` — ${p.reason}` : ""}`}
                style={{ fontSize: "0.68rem", fontWeight: 600, color: pc.color, background: pc.bg, borderRadius: 10, padding: "1px 8px" }}>{pc.label}{p.stateSource === "manual" ? " ✎" : ""}</span>}
              <span className="d-flex" style={{ gap: 3 }}>
                {m.nudges.map((n, i) => (
                  <span key={i} title={`#${n.index} ${TAB[n.tab] || n.tab} · ${n.status} · ${fmt(n.at)}`}
                    style={{ fontSize: "0.62rem", fontWeight: 600, padding: "1px 6px", borderRadius: 6, border: `1px solid ${STATUS_COLOR[n.status] || "#98a2b3"}`,
                      color: n.status === "success" ? "#fff" : STATUS_COLOR[n.status] || "#98a2b3", background: n.status === "success" ? STATUS_COLOR.success : "transparent" }}>
                    {TAB[n.tab] || n.tab || `#${n.index}`}
                  </span>
                ))}
              </span>
              <span className="small" data-testid="nudge-phase" style={{ color: ph.color }}>{ph.label}{m.phase === "blocked" ? ` (${blockedLabel(m.blockedBy)})` : ""}</span>
              <span className="text-muted small ml-auto">{fmt(m.lastAt)}</span>
            </div>
            {isOpen && (
              <div style={{ marginTop: 6 }}>
                {m.nudges.map((n, i) => (
                  <div key={i} className="small" style={{ borderLeft: `3px solid ${STATUS_COLOR[n.status] || "#98a2b3"}`, paddingLeft: 8, marginBottom: 6 }}>
                    <b>Nudge {n.index}</b> · {TAB[n.tab] || n.tab} · {n.status} · {fmt(n.at)}{n.error ? <span style={{ color: "#b42318" }}> — {n.error}</span> : null}
                    <div style={{ whiteSpace: "pre-wrap", color: "#475467" }}>{n.text}</div>
                  </div>
                ))}
                {m.handle && <a className="small" href={`https://www.skool.com/@${m.handle}`} target="_blank" rel="noreferrer">@{m.handle}</a>}
                {pc ? (
                  <div data-testid="nudge-prospect-block" style={{ marginTop: 6, padding: 8, borderRadius: 8, background: pc.bg, fontSize: "0.78rem" }}>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <b style={{ color: pc.color }}>{pc.label}</b>
                      <span className="text-muted">· {SOURCE_LABEL[p.stateSource] || p.stateSource}{p.introduced ? " · introduced" : ""}</span>
                      <select className="form-control form-control-sm ml-auto" style={{ width: 180 }} aria-label="Change prospect state" value=""
                        onChange={(e) => changeState(p, e.target.value)}>
                        <option value="">Change state…</option>
                        {PROSPECT_STATES.filter((x) => x.key !== p.state || p.stateSource !== "manual").map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                        {p.stateSource === "manual" && <option value="auto">Back to automatic</option>}
                      </select>
                    </div>
                    {p.reason && <div style={{ color: "#475467" }}>{p.reason}</div>}
                    {p.bookingAt && <div style={{ color: "#6a3fb5" }}>Call booked: {fmt(p.bookingAt)}</div>}
                    <div className="text-muted">{pc.hint}</div>
                  </div>
                ) : <div className="text-muted small" style={{ marginTop: 6 }}>Not tracked — no longer a member, or not classified yet.</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
